import React, { createContext, useContext, useState, ReactNode } from 'react';
import { SystemHealth } from '../services/warmupService';

interface WarmupContextType {
    isWarmupComplete: boolean;
    systemHealth: SystemHealth | null;
    setWarmupComplete: (health: SystemHealth) => void;
    resetWarmup: () => void;
}

const WarmupContext = createContext<WarmupContextType | undefined>(undefined);

interface WarmupProviderProps {
    children: ReactNode;
}

export const WarmupProvider: React.FC<WarmupProviderProps> = ({ children }) => {
    const [isWarmupComplete, setIsWarmupComplete] = useState(false);
    const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);

    const setWarmupComplete = (health: SystemHealth) => {
        setSystemHealth(health);
        setIsWarmupComplete(true);
        
        // Store the warmup status in localStorage to avoid repeated checks
        localStorage.setItem('warmupComplete', 'true');
        localStorage.setItem('lastWarmupTime', new Date().toISOString());
        localStorage.setItem('systemHealth', JSON.stringify(health));
    };

    const resetWarmup = () => {
        setIsWarmupComplete(false);
        setSystemHealth(null);
        localStorage.removeItem('warmupComplete');
        localStorage.removeItem('lastWarmupTime');
        localStorage.removeItem('systemHealth');
    };

    // Check if warmup was completed recently (within last 5 minutes)
    React.useEffect(() => {
        const warmupComplete = localStorage.getItem('warmupComplete');
        const lastWarmupTime = localStorage.getItem('lastWarmupTime');
        const storedHealth = localStorage.getItem('systemHealth');

        if (warmupComplete && lastWarmupTime && storedHealth) {
            const lastWarmup = new Date(lastWarmupTime);
            const now = new Date();
            const timeDiff = now.getTime() - lastWarmup.getTime();
            const minutesDiff = timeDiff / (1000 * 60);

            // If warmup was completed within the last 5 minutes, skip it
            if (minutesDiff < 5) {
                try {
                    const health = JSON.parse(storedHealth);
                    setSystemHealth(health);
                    setIsWarmupComplete(true);
                } catch (error) {
                    // If parsing fails, reset warmup
                    resetWarmup();
                }
            } else {
                // Clear old warmup data
                resetWarmup();
            }
        }
    }, []);

    return (
        <WarmupContext.Provider value={{
            isWarmupComplete,
            systemHealth,
            setWarmupComplete,
            resetWarmup
        }}>
            {children}
        </WarmupContext.Provider>
    );
};

export const useWarmup = (): WarmupContextType => {
    const context = useContext(WarmupContext);
    if (context === undefined) {
        throw new Error('useWarmup must be used within a WarmupProvider');
    }
    return context;
};
